import { supabase } from '@shared/lib/supabase';

export interface SubscriptionTier {
  id: string;
  name: string;
  slug: string;
  stripe_price_id: string | null;
  monthly_price: number;
  annual_price: number | null;
  features: string[];
  quota_limits: {
    report_runs: number;
    ai_coach_messages: number;
    job_match_analyses: number;
  };
  display_order: number;
  is_active: boolean;
  description: string | null;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  tier: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  cancellation_reason: string | null;
}

export interface UsageQuota {
  exists: boolean;
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  resets_at?: string;
  is_admin?: boolean;
  unlimited?: boolean;
}

/**
 * Get all available subscription tiers
 */
export async function getSubscriptionTiers(): Promise<SubscriptionTier[]> {
  const { data, error } = await supabase
    .from('subscription_tiers')
    .select('*')
    .eq('is_active', true)
    .order('display_order');

  if (error) {
    console.error('Error fetching subscription tiers:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get user's current subscription
 */
export async function getUserSubscription(userId: string): Promise<UserSubscription | null> {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user subscription:', error);
    throw error;
  }

  return data;
}

/**
 * Check if user has access to a specific feature
 */
export async function checkFeatureAccess(
  userId: string,
  featureKey: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_feature_access', {
    user_id_param: userId,
    feature_key_param: featureKey,
  });

  if (error) {
    console.error('Error checking feature access:', error);
    return false;
  }

  return data === true;
}

/**
 * Get user's usage quota for a specific type
 */
export async function getUsageQuota(
  userId: string,
  quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs'
): Promise<UsageQuota> {
  const { data, error } = await supabase.rpc('get_usage_quota', {
    user_id_param: userId,
    quota_type_param: quotaType,
  });

  if (error) {
    console.error('Error fetching usage quota:', error);
    return {
      exists: false,
      used: 0,
      limit: 0,
      remaining: 0,
      percentage: 0,
    };
  }

  return data as UsageQuota;
}

/**
 * Increment user's usage quota
 */
export async function incrementUsageQuota(
  userId: string,
  quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs',
  incrementBy: number = 1
): Promise<{ success: boolean; used: number; limit: number; remaining: number }> {
  const { data, error } = await supabase.rpc('increment_usage_quota', {
    user_id_param: userId,
    quota_type_param: quotaType,
    increment_by: incrementBy,
  });

  if (error) {
    console.error('Error incrementing usage quota:', error);
    throw error;
  }

  return data;
}

/**
 * Create Stripe checkout session for setup fee
 */
export async function createSetupCheckout(
  intendedTier: string,
  successUrl?: string,
  cancelUrl?: string
): Promise<{ sessionId: string; url: string }> {
  const { data, error } = await supabase.functions.invoke('stripe-create-setup-checkout', {
    body: {
      intendedTier,
      successUrl,
      cancelUrl,
    },
  });

  if (error) {
    console.error('Error creating setup checkout:', error);
    throw error;
  }

  return data;
}

/**
 * Create Stripe Customer Portal session
 */
export async function createPortalSession(returnUrl?: string): Promise<{ url: string }> {
  const { data, error } = await supabase.functions.invoke('stripe-create-portal-session', {
    body: {
      returnUrl,
    },
  });

  if (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }

  return data;
}

/**
 * Get user's subscription tier from profile
 */
export async function getUserTier(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('subscription_tier')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user tier:', error);
    return null;
  }

  return data?.subscription_tier || null;
}

/**
 * Check if user has paid setup fee
 */
export async function hasSetupFeePaid(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_setup_fee_paid', {
    user_id_param: userId,
  });

  if (error) {
    console.error('Error checking setup fee:', error);
    return false;
  }

  return data === true;
}

/**
 * Get subscription status summary
 */
export async function getSubscriptionStatus(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('subscription_tier, subscription_status, setup_fee_paid_at, current_period_end, cancel_at_period_end')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching subscription status:', error);
    return null;
  }

  return data;
}

/**
 * Feature gate keys mapped to required tiers
 */
export const FEATURE_GATES = {
  SKILLS_TRACKER: 'skills_tracker',
  AI_COACH: 'ai_coach',
  JOB_MATCH_AI: 'job_match_ai',
  VOICE_FEATURES: 'voice_features',
  ADVANCED_REPORTS: 'advanced_reports',
} as const;

/**
 * Tier hierarchy for comparison
 */
export const TIER_HIERARCHY = {
  starter: 1,
  growth: 2,
  accelerator: 3,
} as const;

/**
 * Check if tier meets requirement
 */
export function tierMeetsRequirement(userTier: string | null, requiredTier: string): boolean {
  if (!userTier) return false;
  const userLevel = TIER_HIERARCHY[userTier as keyof typeof TIER_HIERARCHY] || 0;
  const requiredLevel = TIER_HIERARCHY[requiredTier as keyof typeof TIER_HIERARCHY] || 0;
  return userLevel >= requiredLevel;
}

/**
 * Check if user has active subscription
 */
export async function hasActiveSubscription(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_active_subscription', {
    user_id_param: userId,
  });

  if (error) {
    console.error('Error checking active subscription:', error);
    return false;
  }

  return data === true;
}

/**
 * Get comprehensive user access status
 */
export async function getUserAccessStatus(userId: string): Promise<{
  has_setup_fee: boolean;
  has_subscription: boolean;
  is_active: boolean;
  current_tier: string;
  subscription_status: string;
  is_expired: boolean;
  requires_action: boolean;
  is_admin?: boolean;
  full_access?: boolean;
}> {
  const { data, error } = await supabase.rpc('get_user_access_status', {
    user_id_param: userId,
  });

  if (error) {
    console.error('Error getting user access status:', error);
    return {
      has_setup_fee: false,
      has_subscription: false,
      is_active: false,
      current_tier: 'none',
      subscription_status: 'none',
      is_expired: false,
      requires_action: true,
      is_admin: false,
      full_access: false,
    };
  }

  return data;
}

/**
 * Check if user is an admin
 * Admins bypass all subscription restrictions
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_user_admin', {
    user_id_param: userId,
  });

  if (error) {
    console.error('Error checking admin status:', error);
    return false;
  }

  return data === true;
}
